import { css } from 'styled-components';

export interface ScrollbarConfig {
  width?: number;
  height?: number;
  trackColor?: string;
  thumbColor?: string;
  thumbHoverColor?: string;
  thumbBorderColor?: string;
  borderRadius?: number;
  trackBorderRadius?: number;
}

const defaultConfig: Required<ScrollbarConfig> = {
  width: 8,
  height: 8,
  trackColor: 'transparent',
  thumbColor: 'rgba(255, 255, 255, 0.2)',
  thumbHoverColor: 'rgba(255, 255, 255, 0.4)',
  thumbBorderColor: 'transparent',
  borderRadius: 4,
  trackBorderRadius: 0,
};

/**
 * Creates scrollbar styles with configurable parameters
 * @param config - Configuration object for scrollbar appearance
 * @returns CSS template literal with scrollbar styles
 */
export const createScrollbarStyles = (config: ScrollbarConfig = {}): ReturnType<typeof css> => {
  const finalConfig = { ...defaultConfig, ...config };
  
  return css`
    /* Firefox */
    scrollbar-width: thin;
    scrollbar-color: ${finalConfig.thumbColor} ${finalConfig.trackColor};

    /* WebKit browsers (Chrome, Safari, Edge) */
    &::-webkit-scrollbar {
      width: ${finalConfig.width}px;
      height: ${finalConfig.height}px;
      background: ${finalConfig.trackColor};
      -webkit-appearance: none;
    }

    &::-webkit-scrollbar:horizontal {
      height: ${finalConfig.height}px;
    }

    /* Hide scrollbar arrows/buttons - comprehensive approach */
    &::-webkit-scrollbar-button {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
      -webkit-appearance: none;
    }

    &::-webkit-scrollbar-button:start:decrement,
    &::-webkit-scrollbar-button:end:increment {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
    }

    &::-webkit-scrollbar-button:vertical:start:decrement,
    &::-webkit-scrollbar-button:vertical:end:increment,
    &::-webkit-scrollbar-button:horizontal:start:decrement,
    &::-webkit-scrollbar-button:horizontal:end:increment {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
    }

    &::-webkit-scrollbar-track {
      background: ${finalConfig.trackColor};
      border-radius: ${finalConfig.trackBorderRadius}px;
    }

    &::-webkit-scrollbar-thumb {
      background: ${finalConfig.thumbColor};
      border-radius: 999px; /* Pill shape - fully rounded ends */
      border: ${finalConfig.thumbBorderColor !== 'transparent' ? `1px solid ${finalConfig.thumbBorderColor}` : 'none'};
      transition: background 0.15s ease;
      min-height: 30px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: ${finalConfig.thumbHoverColor};
    }

    &::-webkit-scrollbar-thumb:active {
      background: ${finalConfig.thumbHoverColor};
    }

    /* Corner between vertical and horizontal scrollbars */
    &::-webkit-scrollbar-corner {
      background: ${finalConfig.trackColor};
    }

    /* Smooth scrolling */
    scroll-behavior: smooth;
  `;
};

/**
 * Default scrollbar styles matching the table design
 * This is the standard scrollbar used throughout the application
 */
export const defaultScrollbarStyles = createScrollbarStyles();

/**
 * Light scrollbar variant - more subtle appearance
 */
export const lightScrollbarStyles = createScrollbarStyles({
  trackColor: 'rgba(255, 255, 255, 0.05)',
  thumbColor: 'rgba(255, 255, 255, 0.3)',
  thumbHoverColor: 'rgba(255, 255, 255, 0.5)',
});

/**
 * Thick scrollbar variant - larger, more visible
 */
export const thickScrollbarStyles = createScrollbarStyles({
  width: 12,
  height: 12,
  borderRadius: 6,
  trackBorderRadius: 10,
});

/**
 * Hidden scrollbar - for containers that need scrolling but no visible scrollbar
 */
export const hiddenScrollbarStyles = css`
  scrollbar-width: none;
  scrollbar-color: transparent transparent;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
  }
`;

